import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Copy, Plus, Loader2, UserPlus, Globe, Check, MessageCircle, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const formatTime = (t) => {
  const [h, m] = t.split(":");
  const hour = parseInt(h);
  return `${hour > 12 ? hour - 12 : hour || 12}:${m} ${hour >= 12 ? "PM" : "AM"}`;
};

const AdminTutors = () => {
  const navigate = useNavigate();
  const [tutors, setTutors] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingLangs, setEditingLangs] = useState(null); // tutor id being edited
  const [selectedLangs, setSelectedLangs] = useState([]);
  const [messaging, setMessaging] = useState(null);
  const [expandedAvail, setExpandedAvail] = useState(null);
  const [availability, setAvailability] = useState({});
  const [loadingAvail, setLoadingAvail] = useState(null);

  useEffect(() => { loadData(); }, []);

  const messageTutor = async (tutor) => {
    setMessaging(tutor.id);
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.post(`${API}/messages/conversations`,
        { recipient_id: tutor.id, recipient_role: "tutor" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate("/admin/messages", { state: { openConversationId: res.data.id } });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to open conversation");
    } finally {
      setMessaging(null);
    }
  };

  const toggleAvailability = async (tutor) => {
    if (expandedAvail === tutor.id) {
      setExpandedAvail(null);
      return;
    }
    setExpandedAvail(tutor.id);
    if (availability[tutor.id]) return;
    setLoadingAvail(tutor.id);
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${API}/tutors/${tutor.id}/availability`, { headers: { Authorization: `Bearer ${token}` } });
      setAvailability((prev) => ({ ...prev, [tutor.id]: res.data }));
    } catch (error) {
      toast.error("Failed to load availability");
    } finally {
      setLoadingAvail(null);
    }
  };

  const loadData = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const [tutorsRes, langsRes] = await Promise.all([
        axios.get(`${API}/tutors`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/languages`)
      ]);
      setTutors(tutorsRes.data);
      setLanguages(langsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const createTutor = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(`${API}/tutors`, { name, email }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Tutor created!");
      setName(""); setEmail(""); setShowForm(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create tutor");
    } finally {
      setCreating(false);
    }
  };

  const deleteTutor = async () => {
    if (!deleteTarget) return;
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/tutors/${deleteTarget}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Tutor deleted");
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error("Failed to delete tutor");
    }
  };

  const startEditLangs = (tutor) => {
    setEditingLangs(tutor.id);
    setSelectedLangs(tutor.assigned_languages || []);
  };

  const toggleLang = (langId) => {
    setSelectedLangs(prev =>
      prev.includes(langId) ? prev.filter(id => id !== langId) : [...prev, langId]
    );
  };

  const saveLangs = async () => {
    try {
      const token = localStorage.getItem("access_token");
      await axios.put(`${API}/tutors/${editingLangs}/languages`, {
        language_ids: selectedLangs
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Languages assigned!");
      setEditingLangs(null);
      loadData();
    } catch (error) {
      toast.error("Failed to assign languages");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Copied!");
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spinner"></div></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2" data-testid="tutors-title">Tutors</h1>
          <p className="text-[#4A4A4A]">Manage tutors, access codes, and language assignments</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="brutalist-btn-primary flex items-center gap-2" data-testid="add-tutor-btn">
          <UserPlus className="w-5 h-5" /> Add Tutor
        </button>
      </div>

      {showForm && (
        <form onSubmit={createTutor} className="brutalist-card p-6 mb-6 space-y-4" data-testid="add-tutor-form">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tutor name" className="brutalist-input" required data-testid="new-tutor-name" />
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="brutalist-input" required data-testid="new-tutor-email" />
          <button type="submit" disabled={creating} className="brutalist-btn-primary flex items-center gap-2" data-testid="create-tutor-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create
          </button>
        </form>
      )}

      {tutors.length === 0 ? (
        <div className="brutalist-card p-12 text-center"><p className="text-[#4A4A4A]">No tutors yet. Add one above.</p></div>
      ) : (
        <div className="space-y-4">
          {tutors.map(t => (
            <div key={t.id} className="brutalist-card p-4" data-testid={`tutor-card-${t.id}`}>
              <div className="flex items-center gap-4">
                {t.photo_url ? (
                  <img src={t.photo_url} alt={t.name} className="w-14 h-14 rounded-full border-2 border-[#0A0A0A] object-cover" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#D6E6FA] border-2 border-[#0A0A0A] flex items-center justify-center text-xl font-bold">
                    {t.name?.charAt(0)}
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-bold">{t.name}</h3>
                  <p className="text-sm text-[#4A4A4A]">{t.email}</p>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {t.profile_complete ? (
                      <span className="text-xs bg-[#D6E6FA] px-2 py-0.5 rounded-full border border-[#0A0A0A] font-bold">Profile complete</span>
                    ) : (
                      <span className="text-xs bg-[#FBE7C6] px-2 py-0.5 rounded-full border border-[#0A0A0A] font-bold">Awaiting setup</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-lg">{t.access_code}</span>
                  <button onClick={() => copyCode(t.access_code)} className="p-2 hover:bg-[#F0EFEB] rounded" data-testid={`copy-code-${t.id}`}>
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => messageTutor(t)}
                    disabled={messaging === t.id}
                    className="p-2 hover:bg-[#F0EFEB] rounded"
                    title="Message this tutor"
                    data-testid={`message-tutor-${t.id}`}
                  >
                    <MessageCircle className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => toggleAvailability(t)}
                    className="p-2 hover:bg-[#F0EFEB] rounded"
                    title="View availability"
                    data-testid={`toggle-availability-${t.id}`}
                  >
                    {expandedAvail === t.id ? <ChevronUp className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                  </button>
                  <button onClick={() => setDeleteTarget(t.id)} className="p-2 text-[#FF6B6B] hover:bg-[#FFAEBC] rounded" data-testid={`delete-tutor-${t.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Read-only Availability View */}
              {expandedAvail === t.id && (
                <div className="mt-3 pt-3 border-t border-[#0A0A0A]/10" data-testid={`availability-view-${t.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-[#4A4A4A]" />
                    <span className="text-sm font-bold text-[#4A4A4A]">Weekly Availability (read-only):</span>
                  </div>
                  {loadingAvail === t.id ? (
                    <p className="text-sm text-[#4A4A4A] italic">Loading...</p>
                  ) : (availability[t.id] || []).length === 0 ? (
                    <p className="text-sm text-[#4A4A4A] italic">No availability set yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {DAY_NAMES.map((dayName, dayIndex) => {
                        const daySlots = (availability[t.id] || []).filter((s) => s.day_of_week === dayIndex);
                        if (daySlots.length === 0) return null;
                        return (
                          <div key={dayIndex} className="flex items-center gap-3 text-sm">
                            <span className="w-24 font-bold">{dayName}</span>
                            <div className="flex flex-wrap gap-2">
                              {daySlots.map((slot) => (
                                <span key={slot.id} className="bg-[#F0EFEB] border border-[#0A0A0A]/20 rounded-full px-3 py-1" data-testid={`admin-avail-slot-${slot.id}`}>
                                  {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Language Assignment */}
              <div className="mt-3 pt-3 border-t border-[#0A0A0A]/10">
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="w-4 h-4 text-[#4A4A4A]" />
                  <span className="text-sm font-bold text-[#4A4A4A]">Assigned Languages:</span>
                </div>
                
                {editingLangs === t.id ? (
                  <div className="flex flex-wrap gap-2 items-center">
                    {languages.map(lang => (
                      <button
                        key={lang.id}
                        onClick={() => toggleLang(lang.id)}
                        className={`px-3 py-1 rounded-full border-2 text-sm font-bold transition-colors ${
                          selectedLangs.includes(lang.id)
                            ? 'bg-[#D6E6FA] border-[#0A0A0A]'
                            : 'bg-white border-[#0A0A0A]/30 text-[#4A4A4A]'
                        }`}
                        data-testid={`lang-toggle-${lang.id}`}
                      >
                        {lang.flag_emoji} {lang.name}
                        {selectedLangs.includes(lang.id) && <Check className="w-3 h-3 ml-1 inline" />}
                      </button>
                    ))}
                    <button onClick={saveLangs} className="brutalist-btn-primary text-xs px-3 py-1" data-testid={`save-langs-${t.id}`}>Save</button>
                    <button onClick={() => setEditingLangs(null)} className="text-xs text-[#4A4A4A] underline">Cancel</button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 items-center">
                    {(t.assigned_language_names || []).length > 0 ? (
                      t.assigned_language_names.map((name, i) => (
                        <span key={i} className="px-3 py-1 rounded-full bg-[#A0D2F8]/30 border border-[#0A0A0A] text-sm font-medium">
                          {name}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-[#4A4A4A] italic">None assigned</span>
                    )}
                    <button
                      onClick={() => startEditLangs(t)}
                      className="text-xs text-[#4A4A4A] underline hover:text-[#0A0A0A]"
                      data-testid={`edit-langs-${t.id}`}
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Tutor?"
        description="This will permanently remove this tutor and their access code. This action cannot be undone."
        onConfirm={deleteTutor}
        destructive
      />
    </div>
  );
};

export default AdminTutors;
